import React, { useMemo } from "react";
import { useParams } from "react-router-dom";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { useQueries } from "@tanstack/react-query";
import { Tile, TileGrid, BigMetric } from "@/components/common/Tile";
import { Loading } from "@/components/common/Loading";
import { Pathogen } from "@/components/common/Pathogen";
import { useDevice } from "@/hooks/useDevice";
import { useOpticsForDevice } from "@/hooks/useOptic";
import { useHeartbeats } from "@/hooks/useDeviceLogs";
import { useQuery } from "@tanstack/react-query";
import { qpcrApi } from "@/api/qpcr";

const DOUGHNUT_COLORS = ["#DC2626", "#2563EB"];

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return "—";
  }
}

function Chip({
  label,
  variant,
}: {
  label: React.ReactNode;
  variant: "success" | "danger" | "warning" | "info" | "purple";
}) {
  const styles: Record<string, string> = {
    success: "bg-success-bg text-success-text",
    danger: "bg-danger-bg text-danger-text",
    warning: "bg-warning-bg text-warning-text",
    info: "bg-info-bg text-info-text",
    purple: "bg-purple-bg text-purple-text",
  };
  return (
    <span className={`inline-flex items-center px-sm py-xs rounded-md text-xs font-medium ${styles[variant]}`}>
      {label}
    </span>
  );
}

function Statement({ title, value }: { title: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-xs border-b border-border">
      <span className="text-sm font-medium text-text">{title}</span>
      <span className="text-sm text-textLight">{value}</span>
    </div>
  );
}

function ReagentDoughnut({ consumed }: { consumed: number }) {
  const data = [
    { name: "Consumed", value: consumed },
    { name: "Available", value: 100 - consumed },
  ];
  return (
    <div className="h-[200px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            innerRadius={45}
            outerRadius={75}
            paddingAngle={2}
            isAnimationActive={false}
          >
            {data.map((_, i) => (
              <Cell key={i} fill={DOUGHNUT_COLORS[i]} />
            ))}
          </Pie>
          <Tooltip contentStyle={{ fontSize: 12 }} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

function NoTestsNotice() {
  return (
    <div className="flex-1 flex items-center justify-center text-sm text-textLight">
      No tests run yet
    </div>
  );
}

const DeviceStatus: React.FC = () => {
  const { deviceId = "" } = useParams<{ deviceId: string }>();
  const deviceQ = useDevice(deviceId);
  const opticsQ = useOpticsForDevice(deviceId, 500);
  const heartbeatsQ = useHeartbeats(deviceId, 1);
  const pathogensQ = useQuery({
    queryKey: ["pathogens"],
    queryFn: () => qpcrApi.pathogens(),
    staleTime: 5 * 60_000,
  });

  const optics = opticsQ.data?.optics ?? [];
  const testCount = optics.length;

  // Pull pathogen names from optic *details* so we surface what was actually
  // run in each test, not just whatever happens to be registered in the
  // pathogens catalog. We fetch detail for up to the most recent 25 optics —
  // enough to populate "current & historical targets" without hammering the
  // backend, and react-query caches per-id so it's cheap on revisits.
  const opticDetailQueries = useQueries({
    queries: optics.slice(0, 25).map(o => ({
      queryKey: ["optic", o.id],
      queryFn: () => qpcrApi.detail(o.id),
      staleTime: 5 * 60_000,
    })),
  });

  const pathogenNameById = useMemo(() => {
    const m = new Map<number, string>();
    for (const p of pathogensQ.data?.pathogens ?? []) m.set(p.id, p.name);
    return m;
  }, [pathogensQ.data]);

  const pathogensTested = useMemo(() => {
    const byId = new Map<number, string>();
    const unknownNames = new Set<string>();
    // 1. From hydrated optic details — has both pathogen_id and pathogen_name.
    for (const dq of opticDetailQueries) {
      const detail = dq.data;
      if (!detail) continue;
      for (const ss of detail.sample_sets ?? []) {
        if (ss.pathogen_id != null && ss.pathogen_name) {
          byId.set(ss.pathogen_id, ss.pathogen_name);
        } else if (ss.pathogen_name) {
          unknownNames.add(ss.pathogen_name);
        }
      }
    }
    // 2. Backfill from optic summaries' pathogen_ids using the registry.
    for (const o of optics) {
      for (const pid of o.pathogen_ids ?? []) {
        if (!byId.has(pid)) {
          const name = pathogenNameById.get(pid);
          if (name) byId.set(pid, name);
        }
      }
    }
    const ided = Array.from(byId.entries()).map(([id, name]) => ({ id, name }));
    const unkeyed = Array.from(unknownNames).map(name => ({ id: -1, name }));
    return [...ided, ...unkeyed];
  }, [opticDetailQueries, optics, pathogenNameById]);

  const latestCapturedAt = useMemo(() => {
    const hb = heartbeatsQ.data?.heartbeats?.[0];
    return hb?.captured_at ?? hb?.received_at ?? null;
  }, [heartbeatsQ.data]);

  if (deviceQ.isLoading) return <Loading />;
  const device = deviceQ.data;
  if (!device) {
    return <div className="text-textLight text-sm">Device not found.</div>;
  }

  return (
    <TileGrid>
      {/* Device State */}
      <div className="col-span-12">
        <Tile title={`Device State: ${device.label ?? device.serial_number}`} className="h-[250px]">
          <div className="flex flex-row gap-md h-full min-h-0">
            <div className="flex-1 flex flex-col gap-sm min-w-0">
              <div className="flex flex-wrap gap-xs">
                {device.is_online ? (
                  <Chip label="Online" variant="success" />
                ) : (
                  <Chip label="Offline" variant="danger" />
                )}
                <Chip label="Idle" variant="purple" />
              </div>
              <div className="flex-1 overflow-auto grid grid-cols-1 sm:grid-cols-2 gap-x-md min-h-0">
                <Statement title="Last Seen" value={fmtDateTime(latestCapturedAt ?? device.last_seen_at)} />
                <Statement title="Last Reload" value="—" />
                <Statement title="Last Test" value={fmtDateTime(optics[0]?.captured_at ?? optics[0]?.received_at)} />
                <Statement title="Last Cleaning" value="—" />
                <Statement title="Next Test" value="—" />
                <Statement title="Next Reload" value="—" />
                <Statement title="Device Model" value={device.model || "—"} />
                <Statement title="Version Number" value={device.registered_version ?? device.target_version ?? "—"} />
              </div>
            </div>
            <div className="hidden sm:flex flex-col items-center justify-center w-[220px]">
              <div className="text-xs text-textLight mb-xs">Current Reagent Consumption (%) · demo data</div>
              <ReagentDoughnut consumed={35} />
            </div>
          </div>
        </Tile>
      </div>

      {/* Targets — xs=5 */}
      <div className="col-span-12 md:col-span-5">
        <Tile title="Current & Historical Targets">
          {pathogensTested.length === 0 ? (
            <div className="text-sm text-textLight">No pathogen targets recorded yet.</div>
          ) : (
            <div className="flex flex-row flex-wrap gap-xs">
              {pathogensTested.map((p, i) => (
                <span
                  key={`${p.id}-${i}`}
                  className="px-sm py-xs rounded-md text-xs bg-info-bg text-info-text"
                >
                  <Pathogen name={p.name} />
                </span>
              ))}
            </div>
          )}
        </Tile>
      </div>

      {/* Online Time — xs=7 */}
      <div className="col-span-12 md:col-span-7">
        <Tile title="Online Time">
          <div className="flex-1 flex items-center text-sm text-textLight">
            Use your device for at least 1 month to begin tracking historical up-time.
          </div>
        </Tile>
      </div>

      {/* Metrics row */}
      <div className="col-span-6 md:col-span-3">
        <Tile title="Tests Executed">
          <BigMetric value={testCount} />
        </Tile>
      </div>
      <div className="col-span-6 md:col-span-3">
        <Tile title="Reagent Efficiency">
          {testCount > 0 ? <BigMetric value="84%" /> : <NoTestsNotice />}
        </Tile>
      </div>
      <div className="col-span-6 md:col-span-3">
        <Tile title="Test Success Rate">
          {testCount > 0 ? <BigMetric value="96%" /> : <NoTestsNotice />}
        </Tile>
      </div>
      <div className="col-span-6 md:col-span-3">
        <Tile title="Lab Hours Saved">
          <BigMetric value={`${testCount * 3} hrs`} />
        </Tile>
      </div>
    </TileGrid>
  );
};

export default DeviceStatus;
